<template>
  <v-card>
      
    <loading
      slot="activator"
      :active.sync="isDeleting"
      :can-cancel="false"
      loader="dots"
      :color="appColor_Progress"
    />

    <!-- MAIN TOOLBAR -->
    <v-toolbar :color="toolbarColor" dense dark flat>
      <v-toolbar-title :class="titleClass"><b>{{ ownerName }}</b> - <i>{{ name }}</i></v-toolbar-title>

      <v-spacer />

      <v-toolbar-items>
        <v-btn
          v-if="!showPlaylistSelector"
          @click="done"
          :color="toolbarColorDark"
          class="elevation-0"
          >
          <span class="subheading font-weight-medium">{{ getExpression('Button', 'Close') }}</span>
        </v-btn>
      </v-toolbar-items>
    </v-toolbar>

    <!-- Active Kiosk Playlist Disclaimer
    <v-row v-if="isActive && isKiosk" justify="center" class="ma-2">
      <v-alert
        type="info"
        border="left"
        color="info"
        colored-border
        class="mb-0 pa-1 px-3 grey lighten-3"
        >
        <span class="body-2" v-html="getExpression('Alert', 'ActivePlaylistAbby')"></span>
      </v-alert>
    </v-row>
    -->
    
    <v-card-text v-if="showPlaylistSelector" class="px-1 mt-2">
      <abby-playlist-selector
        v-bind:mediaPathTypes="mediaPathTypes"
        v-bind:mediaPathStatuses="mediaPathStatuses"
        v-bind:toolbarColor="toolbarColor"
        v-bind:toolbarColorDark="toolbarColorDark"
        v-bind:defaultPathTypeId="mediaPathTypeId"
        v-bind:isKiosk="isKiosk"
        v-on:cancel="cancelPlaylistSelector"
        v-on:select="selectPlaylist"
      />
    </v-card-text>

    <template v-else>
      <v-alert
        dense
        text
        dark
        color="info"
        class="my-1 pb-2"
        >
        <v-row align="center" no-gutters>
          <span :class="descriptionClass">{{ description }}</span>
          <v-btn small color="info" class="ml-3 elevation-1" @click="showPlaylistSelector = true">
            {{ getExpression('Label', 'ChangeSelection') }}
          </v-btn>
        </v-row>
      </v-alert>

      <!-- Filters -->
      <v-toolbar flat dense class="my-2">
        <v-card-text class="px-0 mt-3">
          <v-row no-gutters>

            <!-- Search Text -->
            <v-text-field
              v-if="!isSmallDevice" 
              v-model="search"
              append-icon="search"
              :label="getExpression('Label', 'Search')"
              hide-details
              class="mr-3"
            />
          </v-row>
        </v-card-text>

        <v-spacer />

        <v-toolbar-items>
          <!-- Delete Button -->
          <v-btn :small="isSmallDevice" :disabled="disableDelete" @click="remove" class="mt-0" :color="appColor_DeleteButton">
            <v-icon color="white">delete</v-icon>
          </v-btn>

          <!-- Add Button Menu-->
          <v-menu offset-y>
            <template v-slot:activator="{ on }">
              <v-btn :dark="isMediaTypeSelected" :color="toolbarColorDark" v-on="on" :disabled="!isMediaTypeSelected" class="elevation-0 pr-1 mt-0 ml-2" :width="addButtonWidth">
                <span v-if="!isSmallDevice" :dark="isMediaTypeSelected" :color="toolbarColorDark" >{{ addButtonText }}</span>
                <v-icon v-else>add</v-icon>
                <v-icon class="ml-2">arrow_drop_down</v-icon>
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="addMedia()">
                <v-list-item-action class="pr-0">
                  <v-icon>cloud_upload</v-icon>
                </v-list-item-action>
                <input
                  v-show="false"
                  type="file"
                  ref="upload"
                  multiple
                  :accept="fileAccept"
                  @change="fileChanged()"
                />
                <v-list-item-title>{{ getExpression('MenuItem', 'FromComputer') }}</v-list-item-title>
              </v-list-item>

              <template v-if="!hideAddFromSharedLibrary">
                <v-divider />

                <v-list-item @click="pickMedia">
                  <v-list-item-action class="pr-0">
                    <v-icon>folder_shared</v-icon>
                  </v-list-item-action>
                  <v-list-item-title>{{ getExpression('MenuItem', 'FromSharedLibrary') }}</v-list-item-title>
                </v-list-item>
              </template>
            </v-list>
          </v-menu>
        </v-toolbar-items>
      </v-toolbar>

      <!-- Data Table -->
      <v-data-table
        v-if="isMediaTypeSelected"
        v-model="selected"
        :headers="headers"
        :items="filteredItems"
        :loading="isLoading"
        :search="search"
        :custom-filter="customFilter"
        :no-data-text="noDataText"
        show-select
        :loading-text="getExpression('Alert', 'LoadingItems')"
        class="pa-1"
        >
        <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate></v-progress-linear>

        <template v-slot:item.thumbnail="{ item }">
          <router-link :to="currentRoute" style="text-decoration: none">
            <span @click="preview(item)">
              <v-img v-if="getMediaType(item.contentType) !== 'audio'" width="50" height="50" :src="getThumbnailUrl(item)" alt=""></v-img>
              <v-icon v-else large color="blue darken-2">volume_up</v-icon>
            </span>
          </router-link>
        </template>

        <template v-slot:item.contentType="{ item }">
          <span class="text-left pl-0">
            <v-icon v-if="getContentTypeAbbr(item.contentType) === 'image'" large>photo</v-icon>
            <v-icon v-if="getContentTypeAbbr(item.contentType) === 'video'" large>movie</v-icon>
            <v-icon v-if="getContentTypeAbbr(item.contentType) === 'audio'" large>audiotrack</v-icon>
          </span>
        </template>

        <template v-slot:item.title="{ item }">
          <router-link v-if="!item.isLinked" :to="currentRoute">
            <span @click="edit(item)">{{ item.title }}</span>
          </router-link>
          <span v-else>{{ item.title }}</span>
        </template>
  <!--
        <template v-slot:item.size="{ item }">
          <span class="text-left">{{ getFormattedSize(item.size, 1) }}</span>
        </template>

        <template v-slot:item.duration="{ item }">
          <span class="text-left">{{ getFormattedDuration(item.duration) }}</span>
        </template>
  -->
        <template v-slot:item.addDate="{ item }">
          <span class="text-left">
            <app-date :date="item.addDate" :format="'MMM-DD-YYYY'"/>
          </span>
        </template>
      </v-data-table>

      <!-- Media Picker -->
      <v-dialog
        v-model="mediaDialog"
        :persistent="true"
        width="900px"
        >
        <abby-media-container
          v-if="id > 0"
          v-bind:isPickerOnly="true"
          v-bind:profileId="id"
          v-bind:sourceId="sourceId"
          v-bind:defaultMediaPathTypeId="mediaPathTypeId"
          v-bind:reloadMedia="reloadDialogMedia"
          v-bind:defaultContainer="defaultContainer"
          v-bind:toolbarColor="toolbarColor"
          v-bind:toolbarColorDark="toolbarColorDark"
          v-on:cancel="mediaDialog = false"
          v-on:ok="addSelectedMedia"
          style="overflow-y: none;"
        >
        </abby-media-container>
      </v-dialog>

      <!-- Media Previewer -->
      <v-dialog v-model="previewDialog" max-width="800px" persistent>
        <media-previewer
          v-bind:type="previewType"
          v-bind:url="previewUrl"
          v-bind:title="previewTitle"
          v-on:close="closePreview"
        />
      </v-dialog>
      
    </template>
  </v-card>
</template>

<script>
import appService from '@/app.service'
import AbbyMediaContainer from '@/components/AbbyMediaContainer'
import MediaPreviewer from '@/components/MediaPreviewer'
import AbbyPlaylistSelector from '@/components/AbbyPlaylistSelector'
import { mapGetters } from 'vuex'
import { AppColor, Container, ThumbnailUrl, AbbyMediaSourceId, Base64Image, Context, MediaPathTypeId, ImageUri, LanguageId, AppStyle } from '@/config.js'
import AppDate from '@/components/App/AppDate'

export default {
  components: {
    AbbyMediaContainer,
    MediaPreviewer,
    AppDate,
    AbbyPlaylistSelector
  },
  name: 'AbbyProfileDetail',
  props: {
    id: {
      type: Number,
      default: -1
    },
    mediaPathTypes: {
      type: Array,
      default: null
    },
    profileDetails: {
      type: Array,
      default: null
    },
    name: {
      type: String,
      default: null
    },
    ownerName: {
      type: String,
      default: null
    },
    kioskId: {
      type: Number,
      default: -1
    },
    subscriptionLevel: {
      type: Number,
      default: null
    },
    sourceId: {
      type: Number,
      default: -1
    },
    defaultContainer: {
      type: String,
      default: null
    },
    isActive: {
      type: Boolean,
      default: false
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {

      // colors
      appColor_Dark: AppColor.Dark,
      appColor_Medium: AppColor.Medium,
      appColor_DeleteButton: AppColor.DeleteButton,
      appColor_Progress: AppColor.Progress,
      appColor_Abby: AppColor.KioskTypeAbbyLight,
      appColor_AbbyDark: AppColor.KioskTypeAbby,
      appColor_Resident: AppColor.ResidentLight,
      appColor_ResidentDark: AppColor.ResidentDark,
      appColor_HyperLink: AppColor.HyperLink,

      // styles
      appStyle_HyperLink: AppStyle.HyperLink,

      // containers
      kioskPrefix: Container.KioskPrefix,
      sharedLibraryContainer: Container.AbbySharedLibrary,

      // source ids
      sourceId_Resident: AbbyMediaSourceId.Resident,
      sourceId_Kiosk: AbbyMediaSourceId.Kiosk,

      // icons
      base64Image_ProcessingIcon: Base64Image.ProcessingIcon,
      base64Image_AudioIcon: Base64Image.AudioIcon,

      // uri
      thumbnailUrl_Abby: ThumbnailUrl.Abby,
      imageUri_AudioVideoPlaceholder: ImageUri.AudioVideoPlaceholder,

      // context
      context_Abby: Context.Abby,

      // path types
      pathTypeId_AudioMusic: MediaPathTypeId.AudioMusic,
      pathTypeId_ImagesGeneral: MediaPathTypeId.ImagesGeneral,
      pathTypeId_VideosTV: MediaPathTypeId.VideosTV,
      pathTypeId_VideosAmbient: MediaPathTypeId.VideosAmbient,
      pathTypeId_VideosPetCat: MediaPathTypeId.VideosPetCat,
      pathTypeId_VideosTurnWheel: MediaPathTypeId.VideosTurnWheel,
      
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      mediaDialog: false,

      showPlaylistSelector: true,
      isProfileModified: false,
      previewDialog: false,
      longDescriptionAlert: false,
      previewType: null,
      previewTitle: null,
      previewUrl: null,
      mediaPathTypeId: null,
      mediaCategegory: null,
      search: '',
      selected: [],

      editedIndex: -1,
      editedMediaFile: {
        filename: null,
        title: null,
        contentType: null,
        size: null,
        duration: null,
        previewUrl: null
      }
    }
  },
  computed: {
    ...mapGetters(['profile', 'isAdmin', 'currentRoute', 'routeStack', 'locale']),
    noDataText () {
      if (this.isLoading) return this.getExpression('Alert', 'OneMoment')

      return this.profileDetails.length === 0
        ? this.getExpression('Alert', 'NoMedia')
        : ''
    },
    isKiosk () {
      const kioskId = this.kioskId !== null && this.kioskId !== undefined
          ? this.kioskId
          : 0
      return kioskId > 0
    },
    filteredItems () {
      const self = this
      return self.profileDetails.filter(value => {
        return self.mediaPathType.id === self.pathTypeId_AudioMusic
              ? value.category === self.mediaPathType.category
              : value.mediaPathTypeId === self.mediaPathType.id
        // return self.isFromResidentDashboard
        //   ? value.category === self.mediaPathType.category
        //   : self.mediaPathType.id === self.pathTypeId_AudioMusic
        //     ? value.category === self.mediaPathType.category
        //     : value.mediaPathTypeId === self.mediaPathType.id
      })
    },
    headers () {
      return [{
          value: 'thumbnail',
          align: 'center',
          width: '5%',
          sortable: false,
          class: "pr-0"
        },
        {
          value: 'contentType',
          align: 'left',
          width: '3%',
          class: 'pr-0'
        },
        {
          text: this.getExpression('Label', 'Title'),
          value: 'title',
          align: 'left',
          width: '35%',
          class: "pr-0"
        },
        {
          text: this.getExpression('Label', 'Source'),
          value: 'containerOwner',
          align: 'left',
          width: '30%',
          class: "pr-0"
        },
        /*
        {
          text: this.getExpression('Label', 'Type'),
          value: 'contentType',
          align: 'left',
          width: '10%',
          class: "pr-0"
        },
        {
          text: this.getExpression('Label', 'Size'),
          value: 'size',
          align: 'left',
          width: '10%',
          class: "pr-0"
        },
        {
          text: this.getExpression('Label', 'Duration'),
          value: 'duration',
          align: 'left',
          width: '10%',
          class: "pr-0"
        },
        */
        {
          text: this.getExpression('Label','Added'),
          value: 'addDate',
          align: 'left',
          width: '20%'
        }
      ]
    },
    reloadDialogMedia () {
      return this.mediaDialog
    },
    mediaPathType () {
      if (this.mediaPathTypes.length === 0) return
      const mediaPathTypeId = this.mediaPathTypeId
      const pathTypes = this.mediaPathTypes.filter(pt => {
        return pt.id === mediaPathTypeId
      })
      return pathTypes.length > 0
        ? pathTypes[0]
        : null
    },
    mediaPathStatuses () {
      if (this.mediaPathTypes.length === 0) return []

      const self = this
      return self.mediaPathTypes.map(pt => { return {
        id: pt.id,
        hasMedia: self.profileDetails.filter(pd => pd.mediaPathTypeId === pt.id).length > 0
      }})
    },
    // isFromResidentDashboard () {
    //   return this.routeStack.includes('ResidentDashboard')
    // },
    addButtonText () {
      if (this.mediaPathType === null) return this.getExpression('Button', 'AddContent')

      const expressionKey = `Add${this.mediaPathType.category}`
      const isAudio = this.mediaPathType.category.toLowerCase() === 'audio'
      return isAudio
        ? this.getExpression('Button', expressionKey)
        : `${this.getExpression('Button', expressionKey)}s`
      
    },
    fileAccept () {
      return this.mediaPathType !== null
        ? this.mediaPathType.allowedTypes
        : ''
    },
    description () {
      return this.mediaPathType !== null
        ? this.mediaPathType.description
        : null
    },
    longDescription () {
      return this.mediaPathType !== null
        ? this.mediaPathType.longDescription
        : null
    },
    isMediaTypeSelected () {
      return this.mediaPathType !== null
    },
    disableDelete () {
      return this.selected.length === 0
    },
    hideAddFromSharedLibrary () {
      if (this.mediaPathType === null) return true
      return !this.mediaPathType.isSharable
    },
    titleClass () {
      return this.isSmallDevice
        ? 'body-2 pr-1'
        : 'pr-1'
    },
    descriptionClass () {
      return !this.isSmallDevice
        ? 'title font-weight-regular'
        : 'body-1'
    },
    addButtonWidth () {
      return this.isSmallDevice
        ? this.profile.languageId === LanguageId.English
          ? '160'
          : '220'
        : ''
    }
  },
  methods: {
    remove () {
      const self = this

      if (self.selected.length === self.filteredItems.length && self.isActive && self.isKiosk) {
        self.$alert.show({
          title: self.getExpression('FormTitle', 'ActionNotPermitted'),
          text: self.getExpression('Alert', 'ActiveKioskPlaylistRule'),
          formColor: 'yellow darken-3',
          icon: 'warning'
        })
        return
      }

      self.$confirm.show({
        title: self.getExpression('FormTitle', 'DeleteItems'),
        text: self.getExpression('Alert', 'XItemsSelected').replace('{0}', self.selected.length),
        onConfirm: () => {

          self.isDeleting = true

          // profile detail ids to delete
          var ids = self.selected.map(pd => pd.id)

          // mediaFileIds to delete
          var mediaFileIds = self.selected
            .filter(pd => pd.container !== self.sharedLibraryContainer)
            .map(pd => pd.mediaFileId)

          const isResident = self.sourceId === self.sourceId_Resident
          const updateByUserId = self.profile.id.replace('auth0|', '')

          appService.deleteProfileDetails(ids, isResident, updateByUserId).then(data => {
            if (data.errorSeverity > 0) {
              self.handleError(data)
            } else {
              data.deletedIds.forEach(id => {
                const deletedItems = self.filteredItems.filter(f => {
                  return f.id === id
                })
                if (deletedItems.length > 0) {
                  const indexDeleted = self.profileDetails.indexOf(deletedItems[0])
                  self.profileDetails.splice(indexDeleted, 1)
                }
              })

              // this can be done async in the background unbeknownst to the user
              if (mediaFileIds.length > 0) {
                self.deleteMediaFiles(mediaFileIds)
              }
            }

            self.selected = []
            self.isDeleting = false
          }).catch(e => { self.handleCriticalError(e) })
        }
      })
    },

    deleteMediaFiles (mediaFileIds) {
      const self = this
      return new Promise((resolve) => {
        appService.deleteAbbyMediaFilesBulk(mediaFileIds).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            resolve()
          }
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    pickMedia () {
      this.mediaDialog = true
    },

    addSelectedMedia ({ selectedMedia }) {
      const self = this
      var profileMedia = []

      self.mediaDialog = false
      self.isSaving = true
      const media = selectedMedia.map(m => m)

      const mediaPromise = media.map(selected => {
        var model = self.getModel(self.id, selected, true)
        model.isLinked = true // indicate that it came from the shared library
        profileMedia.unshift(model)
      })

      Promise.all(mediaPromise).then(() => {
        const isResident = self.sourceId === self.sourceId_Resident
        appService.addProfileDetails(profileMedia, isResident).then(data => {
          if (data.errorSeverity > 0) {
            self.handleError(data)
          } else {
            data.profileDetails.forEach(pd => {
              appService.getAbbyMediaById(pd.mediaFileId).then(data =>{
                const detailItem = data.mediaFile
                const isLinked = detailItem.container === self.sharedLibraryContainer
                detailItem.isLinked = isLinked
                detailItem.containerOwner = isLinked ? 'Shared Library' : self.ownerName
                detailItem.id = pd.id
                var model = self.getModel(self.id, detailItem, false)
                self.profileDetails.unshift(model)
              })
            })

            self.isProfileModified = true
          }
          self.isSaving = false
        }).catch(e => { self.handleCriticalError(e) })
      })
    },

    getModel (profileId, item, foradd) {
      return {
        profileId: profileId,
        mediaFileId: item.mediaFileId,
        addDate: item.createDate,
        containerId: item.containerId,
        container: item.container,
        containerOwner: item.containerOwner,
        isLinked: item.isLinked,
        filename: item.filename,
        title: item.title,
        mediaPathTypeId: item.mediaPathTypeId,
        mediaPath: item.mediaPath,
        category: item.category,
        responseTypeId: item.responseTypeId,
        contentType: item.contentType,
        size: item.size,
        duration: item.duration,
        id: foradd ? 0 : item.id,
        createDate: item.createDate,
        thumbnail: foradd ? '' : item.thumbnail,
        isProcessing: item.isProcessing
      }
    },

    edit (item) {
      const self = this

      self.editedIndex = this.profileDetails.findIndex(pd => pd.id === item.id)
      self.editedMediaFile = Object.assign({}, item)

      const contentType = self.mediaPathType.category.toLowerCase()
      self.editedMediaFile.previewUrl = contentType !== 'audio'
        ? item.thumbnail
        : self.imageUri_AudioVideoPlaceholder

      self.$media.edit({
        context: self.context_Abby,
        contentType: contentType,
        blob: self.editedMediaFile,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (mediaFile) => {
          self.profileDetails[self.editedIndex].title = mediaFile.title
        }
      })
    },
    
    done () {
      this.selected = []
      this.$emit('done', { profileDetails: this.profileDetails })
      this.isProfileModified = false
      this.mediaPathTypeId = null
      this.showPlaylistSelector = true
    },

    addMedia () {
      const self = this

      const contentType = self.mediaPathType.category.toLowerCase()

      self.$media.add({
        context: self.context_Abby,
        contentType: contentType,
        container: self.defaultContainer,
        profileId: self.id,
        kioskId: self.kioskId,
        mediaPathTypeId: self.mediaPathTypeId,
        allowedTypes: self.mediaPathType.allowedTypes,
        toolbarColor: self.toolbarColor,
        toolbarColorDark: self.toolbarColorDark,

        onSubmit: (uploadResults) => {
          if (uploadResults.length > 0) {
            uploadResults.forEach(result => {
              if (result.success) {
                var detailModel = self.getModel(self.id, result.mediaFile, true)
                  detailModel.id = result.mediaFile.profileDetailId
                  detailModel.isLinked = false
                self.profileDetails.unshift(detailModel)
              }
            })
          }
        }
      })
    },

    preview (item) {
      const self = this
      if (!item.isProcessing) {
        const blobName = `${item.mediaPath}/${item.filename}`
        appService.getAbbyBlobUrl(item.container, blobName).then(data => {
          const previewType = self.mediaPathType.category.toLowerCase()
          self.previewUrl = data
          self.previewType = previewType
          self.previewTitle = item.title
          self.previewDialog = true
        }).catch(e => { self.handleCriticalError(e) })
      } else {
        self.$alert.show({
          title: self.getExpression('FormTitle', 'VideoConversionInProgress'),
          text: self.getExpression('Alert', 'VideoBeingProcessedText'),
          formColor: 'yellow darken-3',
          icon: 'warning'
        })
      }

    },

    closePreview () {
      this.previewType = null
      this.previewUrl = null
      this.previewDialog = false
    },

    getThumbnailUrl (item) {   
      if (item.isProcessing === true) {
        const url = this.base64Image_ProcessingIcon
        item.thumbnail = url

        return url
      } else if (item.category !== 'Audio') {
        const filename = item.filename
        const ext = filename.split('.').pop()
        const thumnailName = filename.replace(ext, 'jpg')
        
        const url = `${this.thumbnailUrl_Abby}/${item.container}/${item.mediaPath}/${thumnailName}`.replace('#', '%23')
        item.thumbnail = url

        return url
      } else {
        const url = this.base64Image_AudioIcon
        item.thumbnail = url
        
        return url
      }
    },

    customFilter (value, search, item) {
      return value != null &&
        search != null &&
        item.title.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
        item.containerOwner.toString().toLowerCase().indexOf(search.toLowerCase()) !== -1
    },

    cancelPlaylistSelector () {
      if (this.mediaPathTypeId > 0) {
        this.showPlaylistSelector = false
      } else {
        this.done()
      }
    },

    selectPlaylist (mediaPathTypeId) {
      this.mediaPathTypeId = mediaPathTypeId
      this.showPlaylistSelector = false
    },

    // events

    handleError (result) {
      this.$error.show({
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  watch: {
    id (newValue, oldValue) {
      if (oldValue !== newValue) {
        this.mediaPathTypeId = null
        this.selected = []
      }
    }
  }
}
</script>
